import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import { API_BASE_URL } from "../../config";
import { redirectToLogin } from "../../auth/session";

type ApiResponse<T = any> = T;

type ApiError = FetchBaseQueryError & { endpointAddress?: string };

interface MutationArgs {
  endpoint: string;
  id?: string | number;
  body?: any;
}

interface UpdateArgs extends MutationArgs {}
interface PatchArgs extends MutationArgs {}
interface RemoveArgs {
  endpoint: string;
  id?: string | number;
}

interface UpdateResponse<T = any> {
  data: T;
  status?: number;
  headers?: Headers;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${API_BASE_URL}/api`,
  headers: { "Content-Type": "application/json charset=utf-8" },
  credentials: "include",
});

// 401 — сессии нет или она завершена (блокировка, смена ролей в ComradeMajor):
// уходим на вход. Если сессия ComradeMajor жива, пользователь вернётся сразу.
const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status === 401) redirectToLogin();
  return result;
};

export const apiSlice = createApi({
  reducerPath: "apiSlice",
  baseQuery,
  endpoints: (builder) => ({
    // GET
    get: builder.query<ApiResponse, string>({
      queryFn: async (endpoint, _queryApi, _options, fetchBaseQuery) => {
        const res = await fetchBaseQuery(endpoint);
        if (res.data) {
          return { data: res.data };
        } else {
          const error = res.error as ApiError;
          error.endpointAddress = endpoint;
          return { error };
        }
      },
    }),
    // POST
    add: builder.mutation<ApiResponse, MutationArgs>({
      query: ({ endpoint, id, body }) => ({
        url: `${endpoint}/${id || ""}`,
        method: "POST",
        body: JSON.stringify(body),
      }),
    }),
    // PUT
    update: builder.mutation<UpdateResponse, UpdateArgs>({
      query: ({ endpoint, id, body }) => ({
        url: `${endpoint}/${id || ""}`,
        method: "PUT",
        body: JSON.stringify(body),
      }),
      transformResponse: (response, meta: FetchBaseQueryMeta) => ({
        data: response,
        status: meta?.response?.status,
        headers: meta?.response?.headers,
      }),
    }),
    // PATCH
    patch: builder.mutation<ApiResponse, PatchArgs>({
      query: ({ endpoint, id, body }) => ({
        url: `${endpoint}/${id || ""}`,
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    }),
    // DELETE
    remove: builder.mutation<ApiResponse, RemoveArgs>({
      query: ({ endpoint, id }) => ({
        url: `${endpoint}/${id || ""}`,
        method: "DELETE",
      }),
    }),
    // Logs-Observer
    getObservedLogs: builder.query<
      Array<{ wellUid: string; wellboreUid: string; logUid: string }>,
      void
    >({
      query: () => "/logs-observer",
    }),

    addObservedLog: builder.mutation<
      void,
      { wellUid: string; wellboreUid: string; logUid: string }
    >({
      query: ({ wellUid, wellboreUid, logUid }) => ({
        url: `/logs-observer/${wellUid}/${wellboreUid}/${logUid}`,
        method: "POST",
      }),
    }),

    removeObservedLog: builder.mutation<
      void,
      { wellUid: string; wellboreUid: string; logUid: string }
    >({
      query: ({ wellUid, wellboreUid, logUid }) => ({
        url: `/logs-observer/${wellUid}/${wellboreUid}/${logUid}`,
        method: "DELETE",
      }),
    }),

    checkObservedStatus: builder.query<
      { isObserved: boolean },
      { wellUid: string; wellboreUid: string; logUid: string }
    >({
      query: ({ wellUid, wellboreUid, logUid }) =>
        `/logs-observer/${wellUid}/${wellboreUid}/${logUid}`,
    }),

    // Вход, текущий пользователь и управление доступом — в accessApi.
  }),
});

export const {
  useGetQuery,
  useAddMutation,
  useUpdateMutation,
  usePatchMutation,
  useRemoveMutation,
  useGetObservedLogsQuery,
  useAddObservedLogMutation,
  useRemoveObservedLogMutation,
  useCheckObservedStatusQuery,
} = apiSlice;
